import { useNavigate } from 'react-router-dom';
import { ChevronsUpDown, Check } from 'lucide-react';
import { useOrganizationContext } from '../../contexts/OrganizationContext';
import { SimpleMenu, SimpleMenuItem } from '../ui/Menu/SimpleMenu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/Avatar';
import { cn } from '../../../shared/utils/cn';

interface OrgSwitcherProps {
  className?: string;
  variant?: 'full' | 'compact';
}

export function OrgSwitcher({ className, variant = 'full' }: OrgSwitcherProps) {
  const navigate = useNavigate();
  const { 
    userContext, 
    activeOrganization, 
    switchOrganization,
    hasMultipleOrgs 
  } = useOrganizationContext();

  if (!userContext || !activeOrganization) {
    return null;
  }

  if (!hasMultipleOrgs) {
    return (
      <div className={cn('flex items-center gap-2.5 px-2 py-2', className)}>
        <Avatar className="h-8 w-8 shrink-0">
          {activeOrganization.icon_url ? (
            <AvatarImage src={activeOrganization.icon_url} alt={activeOrganization.name} />
          ) : null}
          <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
            {getOrgInitials(activeOrganization.name)}
          </AvatarFallback>
        </Avatar>
        {variant === 'full' && (
          <div className="flex-1 text-left min-w-0">
            <p className="text-sm font-medium truncate">{activeOrganization.name}</p>
            <p className="text-xs text-muted-foreground capitalize">{activeOrganization.type}</p>
          </div>
        )}
      </div>
    );
  }

  const handleSwitch = (orgId: string, slug: string) => {
    switchOrganization(orgId);
    navigate(`/org/${slug}`);
  };

  return (
    <SimpleMenu
      align="left"
      className="w-full"
      trigger={
        <button
          className={cn(
            'flex items-center gap-2.5 w-full rounded-lg px-2 py-2 text-left hover:bg-accent transition-colors',
            variant === 'compact' && 'w-10 justify-center',
            className
          )}
        >
          <Avatar className="h-8 w-8 shrink-0">
            {activeOrganization.icon_url ? (
              <AvatarImage src={activeOrganization.icon_url} alt={activeOrganization.name} />
            ) : null}
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
              {getOrgInitials(activeOrganization.name)}
            </AvatarFallback>
          </Avatar>
          {variant === 'full' && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{activeOrganization.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{activeOrganization.type}</p>
              </div>
              <ChevronsUpDown className="h-4 w-4 text-muted-foreground shrink-0" />
            </>
          )}
        </button>
      }
      menuClassName="w-64"
    >
      <div className="p-1">
        {userContext.organizations.map((membership) => {
          const org = membership.organization;
          const isActive = org.id === activeOrganization.id;
          
          return (
            <SimpleMenuItem
              key={org.id}
              onClick={() => handleSwitch(org.id, org.slug)}
              className={cn(
                'flex items-center gap-2.5 px-2 py-2 rounded-md',
                isActive && 'bg-accent'
              )}
            >
              <Avatar className="h-8 w-8 shrink-0">
                {org.icon_url ? (
                  <AvatarImage src={org.icon_url} alt={org.name} />
                ) : null}
                <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-medium">
                  {getOrgInitials(org.name)}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{org.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{org.type}</p>
              </div>
              
              {isActive && (
                <Check className="h-4 w-4 text-primary shrink-0" />
              )}
            </SimpleMenuItem>
          );
        })}
      </div>
    </SimpleMenu>
  );
}

function getOrgInitials(name: string) {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

